import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar, Clock, ExternalLink, BookOpen, Newspaper, ArrowLeft } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Articles = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('Tout');
  const navigate = useNavigate();
  const { t, lang, switchLanguage } = useLanguage();

  const categories = ['Tout', 'Management/Leadership', 'Coaching', 'Actualité'];

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const params = activeFilter !== 'Tout' ? { category: activeFilter } : {};
      const response = await axios.get(`${API}/articles/`, { params });
      setArticles(response.data);
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArticles();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(lang === 'fr' ? 'fr-FR' : 'en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }).format(date);
  };

  const handleArticleClick = (article) => {
    if (article.article_type === 'news' && article.external_url) {
      window.open(article.external_url, '_blank');
    } else {
      navigate(`/articles/${article.slug}`);
    }
  };

  const getCategoryLabel = (idx) => {
    if (idx === 0) return t.articles.all;
    return categories[idx];
  };

  return (
    <div className="articles-page">
      {/* Top Bar */}
      <div className="articles-back-button-container">
        <Button
          variant="ghost"
          className="articles-back-button"
          onClick={() => navigate('/')}
          data-testid="articles-back-btn"
        >
          <ArrowLeft size={20} />
          {t.articles.backToHome}
        </Button>
        <div className="language-switcher" data-testid="articles-lang-switcher">
          <button onClick={() => switchLanguage('fr')} className={`lang-flag ${lang === 'fr' ? 'active' : ''}`} title="Français" aria-label="Français">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 900 600" width="28" height="19"><rect width="900" height="600" fill="#ED2939"/><rect width="600" height="600" fill="#fff"/><rect width="300" height="600" fill="#002395"/></svg>
          </button>
          <button onClick={() => switchLanguage('en')} className={`lang-flag ${lang === 'en' ? 'active' : ''}`} title="English" aria-label="English">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 30" width="28" height="19"><clipPath id="s2"><path d="M0,0 v30 h60 v-30 z"/></clipPath><clipPath id="t2"><path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z"/></clipPath><g clipPath="url(#s2)"><path d="M0,0 v30 h60 v-30 z" fill="#012169"/><path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6"/><path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#t2)" stroke="#C8102E" strokeWidth="4"/><path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10"/><path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6"/></g></svg>
          </button>
        </div>
      </div>

      {/* Header */}
      <section className="articles-header">
        <div className="articles-header-content">
          <h1 className="articles-title">{t.articles.title}</h1>
          <p className="articles-subtitle">{t.articles.subtitle}</p>
        </div>
      </section>

      {/* Filters */}
      <section className="articles-filters-section">
        <div className="articles-filters">
          {categories.map((category, idx) => (
            <Button
              key={category}
              onClick={() => setActiveFilter(category)}
              className={`filter-button ${activeFilter === category ? 'active' : ''}`}
              variant={activeFilter === category ? 'default' : 'outline'}
              data-active={activeFilter === category}
            >
              {getCategoryLabel(idx)}
            </Button>
          ))}
        </div>
      </section>

      {/* Articles Grid */}
      <section className="articles-grid-section">
        <div className="articles-container">
          {loading ? (
            <div className="articles-loading">
              <p>{t.articles.loading}</p>
            </div>
          ) : articles.length === 0 ? (
            <div className="articles-empty">
              <p>{t.articles.empty}</p>
            </div>
          ) : (
            <div className="articles-grid">
              {articles.map((article) => (
                <Card
                  key={article.id}
                  className={`article-card ${article.article_type === 'news' ? 'news-card' : 'article-original-card'}`}
                  onClick={() => handleArticleClick(article)}
                >
                  {article.image_url && (
                    <div className="article-image-container">
                      <img src={article.image_url} alt={article.title} className="article-image" />
                      <div className="article-type-badge">
                        {article.article_type === 'news' ? (
                          <><Newspaper size={16} /> {t.articles.news}</>
                        ) : (
                          <><BookOpen size={16} /> {t.articles.article}</>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div className="article-card-content">
                    <div className="article-meta">
                      <span className="article-category">{article.category}</span>
                      {article.article_type === 'news' && (
                        <ExternalLink size={14} className="external-icon" />
                      )}
                    </div>
                    
                    <h3 className="article-card-title">{article.title}</h3>
                    <p className="article-excerpt">{article.excerpt}</p>
                    
                    <div className="article-footer">
                      <div className="article-info">
                        <span className="info-item">
                          <Calendar size={14} />
                          {formatDate(article.published_at)}
                        </span>
                        {article.article_type === 'article' && (
                          <span className="info-item">
                            <Clock size={14} />
                            {article.reading_time} {t.articles.min}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Articles;
